class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // If Globo (or another app) added items without going through renderContents,
    // the is-empty class may still be set. Re-fetch the cart-drawer section to
    // get the real state before opening, then let renderContents handle the rest.
    if (this.classList.contains('is-empty')) {
      fetch(`${window.Shopify.routes.root}?sections=cart-drawer`)
        .then((response) => response.json())
        .then((parsedState) => {
          this.querySelector('.drawer__inner').classList.remove('is-empty');
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (sectionElement) {
              sectionElement.innerHTML = this.getSectionInnerHTML(
                parsedState[section.id],
                section.selector
              );
            }
          });
        })
        .catch(() => {
          // Fetch failed — still open the drawer rather than silently failing
          this.classList.remove('is-empty');
        })
        .finally(() => {
          this._openDrawer();
        });
      return; // Don't fall through — _openDrawer will be called after fetch
    }

    this._openDrawer();
  }

  _openDrawer() {
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// GPO Parent-Child Cart Sync

(function () {
  // CartDrawerItems is defined above but not a global — grab it from the registry
  // after the element is defined. We patch via the prototype directly.
  const patchCartDrawerItems = function () {
    const CartDrawerItemsProto = customElements.get('cart-drawer-items').prototype;
    const originalUpdateQuantity = CartDrawerItemsProto.updateQuantity
      || customElements.get('cart-items').prototype.updateQuantity;

    CartDrawerItemsProto.updateQuantity = function (line, quantity, event, name, variantId) {
      // Only intercept removals (quantity === 0)
      if (quantity !== 0) {
        return originalUpdateQuantity.apply(this, arguments);
      }

      const cartDrawerItemsEl = this;

      // Read the cart first so we know what's being removed before Dawn wipes it
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          const removedItem = cart.items[line - 1];
          const groupId = removedItem && removedItem.properties
            ? removedItem.properties['_gpo_product_group']
            : null;

          // Always run Dawn's original removal
          const originalResult = originalUpdateQuantity.apply(cartDrawerItemsEl, [line, quantity, event, name, variantId]);

          // If this item has no GPO group ID, nothing more to do
          if (!groupId) return;

          const groupIdStr = String(groupId).trim();

          // Wait for Dawn's removal + re-render to complete, then remove children
          Promise.resolve(originalResult).then(() => {
            fetch('/cart.js')
              .then((r) => r.json())
              .then((updatedCart) => {
                const childUpdates = {};
                updatedCart.items.forEach((item) => {
                  const parentGroup = (item.properties || {})['_gpo_parent_product_group'];
                  if (parentGroup && String(parentGroup).trim() === groupIdStr) {
                    childUpdates[item.key] = 0;
                  }
                });

                if (Object.keys(childUpdates).length === 0) return;

                const sections = cartDrawerItemsEl.getSectionsToRender().map((s) => s.section);

                fetch(routes.cart_update_url, {
                  ...fetchConfig(),
                  body: JSON.stringify({
                    updates: childUpdates,
                    sections: sections,
                    sections_url: window.location.pathname,
                  }),
                })
                  .then((r) => r.json())
                  .then((parsedState) => {
                    cartDrawerItemsEl.getSectionsToRender().forEach((section) => {
                      const el = document.getElementById(section.id)
                        ? (document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id))
                        : document.querySelector(section.selector);

                      if (el && parsedState.sections[section.section]) {
                        el.innerHTML = cartDrawerItemsEl.getSectionInnerHTML(
                          parsedState.sections[section.section],
                          section.selector
                        );
                      }
                    });

                    const cartDrawerWrapper = document.querySelector('cart-drawer');
                    if (cartDrawerWrapper) {
                      cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
                    }

                    publish(PUB_SUB_EVENTS.cartUpdate, {
                      source: 'cart-items',
                      cartData: parsedState,
                    });
                  });
              });
          });
        });
    };
  };

  // cart-drawer-items may not be registered yet if this script runs early,
  // so wait for it to be defined before patching
  if (customElements.get('cart-drawer-items')) {
    patchCartDrawerItems();
  } else {
    customElements.whenDefined('cart-drawer-items').then(patchCartDrawerItems);
  }
})();